import { z } from 'zod';
import { defineEndpoint } from '../define';
import { DateFormatSchema, TimeFormatSchema, TimeZoneSchema } from './preferences';

// GitHub username rules: 1-39 alphanumerics/hyphens, no leading/trailing or double hyphen.
export const GITHUB_USERNAME_RE = /^[a-zA-Z0-9](?:[a-zA-Z0-9]|-(?=[a-zA-Z0-9])){0,38}$/;

// Accept a pasted profile URL ("https://github.com/octocat") or "@octocat"
// and reduce it to the bare username.
export const normalizeGithubUsername = (raw: string): string =>
  raw
    .trim()
    .replace(/^(?:https?:\/\/)?(?:www\.)?github\.com\//i, '')
    .replace(/^@/, '')
    .replace(/[/?#].*$/, '');

export const UpdateMeBodySchema = z
  .object({
    displayName: z.string().trim().min(1).max(100).optional(),
    // null clears the value.
    githubUsername: z
      .string()
      .trim()
      .regex(GITHUB_USERNAME_RE, 'Invalid GitHub username')
      .nullable()
      .optional(),
    // null clears back to Auto (browser default).
    dateFormat: DateFormatSchema.nullable().optional(),
    timeFormat: TimeFormatSchema.nullable().optional(),
    timezone: TimeZoneSchema.nullable().optional(),
    // One-way: marks the first-entry welcome as seen; the server sets
    // welcomed_at = now() once and keeps the first timestamp on replays.
    welcomed: z.literal(true).optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: 'At least one editable field must be present',
  });

export type UpdateMeBody = z.infer<typeof UpdateMeBodySchema>;

export const UpdateMeResponseSchema = z.object({
  data: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    githubUsername: z.string().nullable(),
    githubName: z.string().nullable(),
    githubAvatarUrl: z.string().nullable(),
    dateFormat: DateFormatSchema.nullable(),
    timeFormat: TimeFormatSchema.nullable(),
    timezone: z.string().nullable(),
  }),
});

export const updateMe = defineEndpoint({
  method: 'PATCH',
  path: '/v1/auth/me',
  params: z.object({}),
  query: z.object({}),
  body: UpdateMeBodySchema,
  response: UpdateMeResponseSchema,
});
